import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getCompanyApiEndpoint,
  getGlobalApiEndpoint,
} from "../../config/config";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import { useAuth } from "../../context/AuthContext";
import rNameLogo from "../../assets/Reppi.svg";
import {
  Paper,
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Image,
  Center,
  Box,
} from "@mantine/core";

function SignUpPage() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    birthday: null,
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [inviteToken, setInviteToken] = useState(null);
  // Company the invite token was issued for - it's what routes the signup call,
  // since there's no session yet to tell us.
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDocumentSigner, setIsDocumentSigner] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Function to decode JWT token (basic decode without verification)
  const decodeToken = token => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  // Effect to handle token from URL
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      setInviteToken(token);
      const decodedToken = decodeToken(token);
      if (decodedToken) {
        const tokenCompany = decodedToken.company ?? decodedToken.company_id;
        if (tokenCompany) {
          setCompany(String(tokenCompany));
        }

        // Auto-populate form fields from token
        setFormData(prev => ({
          ...prev,
          firstName: decodedToken.firstName || decodedToken.first_name || "",
          lastName: decodedToken.lastName || decodedToken.last_name || "",
          email: decodedToken.email || "",
          phone: decodedToken.phone_number
            ? formatPhone(decodedToken.phone_number)
            : prev.phone,
        }));

        // Show onboarding message if user has existing data (from invite/document)
        if (decodedToken.rep_id && decodedToken.rep_id > 0) {
          setIsDocumentSigner(true);
        }
      }
    }
  }, [searchParams]);

  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = phone => {
    // Remove all non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, "");
    // Check if it's 10 digits (US format) or 11 digits (with country code)
    return cleanPhone.length === 10 || cleanPhone.length === 11;
  };

  const formatPhone = phone => {
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX for 10 digits
    if (cleanPhone.length === 10) {
      return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
    }
    // Format as +X (XXX) XXX-XXXX for 11 digits
    else if (cleanPhone.length === 11) {
      return `+${cleanPhone.slice(0, 1)} (${cleanPhone.slice(1, 4)}) ${cleanPhone.slice(4, 7)}-${cleanPhone.slice(7)}`;
    }

    return phone;
  };

  const handleChange = (field, value) => {
    // Format phone number as user types
    if (field === "phone") {
      const formattedPhone = formatPhone(value);
      setFormData({
        ...formData,
        [field]: formattedPhone,
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    const newErrors = {};

    // Validate email
    if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate phone
    if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number (10 or 11 digits)";
    }

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Validate password length
    if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      // Prepare user data for API call
      const userData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formData.phone,
        birthday: formData.birthday,
        password: formData.password,
      };

      // Make HTTP POST request to create user
      const apiEndpoint = getCompanyApiEndpoint(company, "/v2/user/add");
      const headers = {
        "Content-Type": "application/json",
      };

      // Add token as authorization if available
      if (inviteToken) {
        headers.Authorization = `Bearer ${inviteToken}`;
      }

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(userData),
      });

      if (response.ok) {
        try {
          const response = await fetch(getGlobalApiEndpoint("/v2/user/login"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include", // Include cookies in the request
            body: JSON.stringify({
              email: userData.email,
              password: userData.password,
              company,
            }),
          });

          await response.json();

          if (response.ok) {
            try {
              const tokenResponse = await fetch(
                getGlobalApiEndpoint("/v2/user/token"),
                {
                  method: "GET",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              );

              if (tokenResponse.ok) {
                const result = await tokenResponse.json();
                const userLoginData = result.data;
                login(userLoginData);

                // Check if user is verified
                if (userLoginData.verified === false) {
                  navigate("/verify-email", {
                    state: { email: userLoginData.email || userData.email },
                  });
                } else {
                  navigate("/training");
                }
              } else {
                // setError("Failed to retrieve user data. Please try again.");
              }
            } catch (tokenError) {
              // setError("Failed to retrieve user data. Please try again.");
            }
          } else {
            // Login failed
            // setError(
            //   data.message || "Login failed. Please check your credentials."
            // );
          }
        } catch (error) {
          // setError("Network error. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        const errorData = await response.json().catch(() => null);

        // CustomPassThroughException messages are written for the user
        // (e.g. "User with this phone number already exists"), so show them
        if (
          errorData?.error === "CustomPassThroughException" &&
          errorData.message
        ) {
          setErrors({ general: errorData.message });
        } else {
          setErrors({ general: "Failed to create account. Please try again." });
        }
        setLoading(false);
      }
    } catch (error) {
      setErrors({
        general: "Network error. Please check your connection and try again.",
      });
      setLoading(false);
    }
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-primary)",
        padding: "20px",
      }}
    >
      <Paper shadow="md" p={60} radius="md">
        <Center>
          <Image
            src={rNameLogo}
            alt="Reppi"
            h={60}
            w="auto"
            style={{ cursor: "pointer", marginBottom: "2rem" }}
            onClick={() => navigate("/home")}
          />
        </Center>

        <Stack gap="md">
          {isDocumentSigner && (
            <Box
              style={{
                padding: "12px 16px",
                backgroundColor: "var(--color-info-bg)",
                border: "1px solid var(--color-info-border)",
                borderRadius: "4px",
                color: "var(--color-info-text)",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              📋 Complete your profile to finish onboarding
            </Box>
          )}

          <DisplayMessage type="error" message={errors.general} />

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="First Name"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={e => handleChange("firstName", e.target.value)}
                  required
                />
                <TextInput
                  label="Last Name"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={e => handleChange("lastName", e.target.value)}
                  required
                />
              </Group>

              <TextInput
                label="Email address"
                placeholder="email@exampledomainname.net"
                type="email"
                value={formData.email}
                onChange={e => handleChange("email", e.target.value)}
                error={errors.email}
                required
              />

              <TextInput
                label="Phone Number"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={e => handleChange("phone", e.target.value)}
                error={errors.phone}
                required
              />

              <TextInput
                label="Birthday"
                placeholder="Select birthday"
                type="date"
                value={formData.birthday || ""}
                onChange={e => handleChange("birthday", e.target.value)}
                error={errors.birthday}
              />

              <PasswordInput
                label="Password"
                placeholder="••••••••••••"
                value={formData.password}
                onChange={e => handleChange("password", e.target.value)}
                error={errors.password}
                required
              />

              <PasswordInput
                label="Confirm Password"
                placeholder="••••••••••••"
                value={formData.confirmPassword}
                onChange={e => handleChange("confirmPassword", e.target.value)}
                error={errors.confirmPassword}
                required
              />

              <Button
                type="submit"
                size="md"
                loading={loading}
                fullWidth
                mt="md"
              >
                Create Account
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Box>
  );
}

export default SignUpPage;
